import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { ShieldCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const DEMO = [
  { role: "Owner", email: "owner@plusplusplus.demo", pw: "Owner!2026" },
  { role: "Admin", email: "admin@plusplusplus.demo", pw: "Admin!2026" },
  { role: "Tender Analyst", email: "analyst@plusplusplus.demo", pw: "Analyst!2026" },
  { role: "Opportunity Manager", email: "manager@plusplusplus.demo", pw: "Manager!2026" },
  { role: "Liaison", email: "liaison@plusplusplus.demo", pw: "Liaison!2026" },
  { role: "Supplier", email: "supplier@plusplusplus.demo", pw: "Supplier!2026" },
];

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const nav = useNavigate();

  const deactivated = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("deactivated") === "1";

  useEffect(() => {
    let active = true;
    api.get("/config/public").then(({ data }) => {
      if (!active) return;
      const allow = !!data?.show_demo_accounts;
      setShowDemo(allow);
      if (allow) { setEmail("owner@plusplusplus.demo"); setPassword("Owner!2026"); }
    }).catch(() => { if (active) setShowDemo(false); });
    return () => { active = false; };
  }, []);

  if (user && user !== false) {
    return <Navigate to={user.role === "supplier" ? "/supplier" : "/"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(u.role === "supplier" ? "/supplier" : "/");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#050505] text-white">
      <div
        className="hidden lg:flex flex-1 relative items-end p-12"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(5,5,5,0.5), rgba(5,5,5,0.92)), url(/assets/login-water.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="scanlines absolute inset-0 pointer-events-none" />
        <div className="relative max-w-md">
          <div className="font-heading font-bold text-6xl tracking-tighter">
            <span className="text-[#FFB300]">+++</span>
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 mt-2">
            TENDER INTELLIGENCE OPERATING SYSTEM
          </div>
          <h2 className="font-heading text-2xl mt-8 leading-tight">
            Discover, qualify, and win government tenders for specialized defense, fire-safety and coatings products.
          </h2>
          <div className="mt-6 text-xs text-zinc-500 tracking-wide">
            <ShieldCheck size={14} className="inline mr-1 text-[#FFB300]" />
            Authorized personnel only. All sessions are logged.
          </div>
        </div>
      </div>

      <div className="flex-1 lg:max-w-[480px] flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Secure access</div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter mb-8">Sign in to +++</h1>

          {deactivated && (
            <div data-testid="login-deactivated-banner" className="text-xs text-amber-300 mb-5 border border-amber-500/40 bg-amber-500/5 px-2.5 py-2">
              Your account is deactivated. Contact an administrator.
            </div>
          )}

          <label className="block mb-4">
            <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">Email</div>
            <input
              data-testid="login-email-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#FFB300]"
            />
          </label>
          <label className="block mb-4">
            <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">Password</div>
            <input
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#FFB300]"
            />
          </label>
          {error && (
            <div data-testid="login-error" className="text-xs text-red-400 mb-3 border border-red-500/40 bg-red-500/5 px-2 py-1.5">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full bg-[#FFB300] text-black font-bold uppercase tracking-widest text-xs py-3 hover:bg-[#FFC133] disabled:opacity-50"
          >
            {loading ? "Authenticating…" : "Sign in"}
          </button>

          {showDemo && (
            <div className="mt-8 border-t border-white/10 pt-4" data-testid="demo-accounts-panel">
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Demo accounts</div>
              <div className="grid grid-cols-1 gap-1 text-[11px] font-mono-data">
                {DEMO.map((d) => (
                  <button
                    type="button"
                    key={d.email}
                    data-testid={`demo-${d.role.toLowerCase().replace(/ /g, "-")}-btn`}
                    onClick={() => { setEmail(d.email); setPassword(d.pw); }}
                    className="flex justify-between items-center px-2 py-1.5 border border-white/5 hover:border-[#FFB300]/40 hover:bg-white/[0.03] text-left"
                  >
                    <span className="text-zinc-300">{d.role}</span>
                    <span className="text-zinc-600">{d.email}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
